import React, { useLayoutEffect, useRef } from 'react'
import { IconMessage, IconTool } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

// How close to the bottom still counts as "following along". Anything further
// up and the operator is reading back, so new text must not yank them down.
const STICK_THRESHOLD_PX = 48

const ROLE_LABEL = { assistant: 'Togo', user: 'You' }

const formatArguments = (args) => {
  if (typeof args !== 'object' || isEmpty(args)) {
    return null
  }
  return Object.entries(args)
    .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join(' · ')
}

const withKeys = (turn) =>
  turn.toolCalls.map((call, index) => ({ ...call, key: `${turn.id}-${index}` }))

// One tool call, inside the turn that made it.
//
// Its own marker rather than more transcript text: a tool call is not something
// anybody said. Reading a conversation you need to see *where* the agent
// reached for something, and a list gathered elsewhere loses exactly that.
const ToolCall = ({ call }) => {
  const args = formatArguments(call.arguments)
  return (
    <li className="voice-agents__conversation-tool">
      <span className="voice-agents__conversation-tool-head">
        <IconTool size={12} aria-hidden="true" />
        <span className="voice-agents__conversation-tool-name">{call.name}</span>
      </span>
      {args && <span className="voice-agents__conversation-tool-args">{args}</span>}
      {/* A null result means the call never returned before the turn ended.
          Saying so beats a blank row that reads as success. */}
      <span
        className={classNames('voice-agents__conversation-tool-result', {
          'voice-agents__conversation-tool-result--pending': isNil(call.result),
        })}
      >
        {isNil(call.result) ? 'no result' : String(call.result)}
      </span>
    </li>
  )
}

// The conversation, turn by turn: the agent down the left, you down the right.
//
// Only the agent's text types itself out, with a caret marking where more is
// coming. A user turn arrives whole — STT commits an utterance in one go, so
// there is no partial text to stream. That asymmetry is upstream's.
const CallConversation = ({ turns, isBusy, isHistoric }) => {
  const scrollRef = useRef(null)
  // Whether to keep pinning to the bottom. A live transcript that always
  // scrolls makes it impossible to read anything but the last line.
  const stickRef = useRef(true)

  const handleScroll = () => {
    const node = scrollRef.current
    if (!node) {
      return
    }
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight
    stickRef.current = distanceFromBottom <= STICK_THRESHOLD_PX
  }

  // Layout effect, not effect: this runs before paint, so a new line is never
  // shown at the old scroll position first.
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (node && stickRef.current) {
      node.scrollTop = node.scrollHeight
    }
  }, [turns])

  // A past call is read from the top — you opened it to find something, not to
  // watch it arrive.
  useLayoutEffect(() => {
    if (isHistoric && scrollRef.current) {
      scrollRef.current.scrollTop = 0
      stickRef.current = false
    }
  }, [isHistoric, turns])

  return (
    <div
      className="voice-agents__conversation"
      ref={scrollRef}
      onScroll={handleScroll}
      data-testid="call-transcript"
    >
      {isEmpty(turns) ? (
        <div className="voice-agents__conversation-empty">
          <span className="voice-agents__conversation-empty-icon" aria-hidden="true">
            <IconMessage size={18} />
          </span>
          <p>
            {isBusy
              ? 'Listening — say something to start.'
              : 'Call the agent to start a conversation.'}
          </p>
        </div>
      ) : (
        turns.map((turn) => (
          <div
            key={turn.id}
            className={classNames(
              'voice-agents__conversation-turn',
              `voice-agents__conversation-turn--${turn.role}`
            )}
            data-testid={`transcript-turn-${turn.role}`}
          >
            <div className="voice-agents__conversation-speaker">
              {ROLE_LABEL[turn.role] || turn.role}
              {/* Barge-in is the difference between "the agent said this" and
                  "the agent got this far" — worth stating, because the text
                  alone reads as a complete sentence either way. */}
              {turn.wasInterrupted && (
                <span className="voice-agents__conversation-interrupted">interrupted</span>
              )}
            </div>
            <p className="voice-agents__conversation-text">
              {turn.text}
              {!turn.isFinal && (
                <span className="voice-agents__conversation-caret" aria-hidden="true" />
              )}
            </p>
            {!isEmpty(turn.toolCalls) && (
              <ul className="voice-agents__conversation-tools" data-testid="turn-tool-calls">
                {/* Keyed on position within the turn, because that is the only
                    identity a tool call has — Cartesia sends no id, and a turn
                    may call the same tool twice. The list is append-only within
                    a turn, so the ordinal is stable. */}
                {withKeys(turn).map((call) => (
                  <ToolCall key={call.key} call={call} />
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  )
}

export default CallConversation
